/* ════════════════════════════════════════════════════════════════════════
   Shoppable Video SDK — widget.js
   One file, two modes:
     • LOCAL  feed → you supply window.SHOPPABLE_FEED (no backend needed)
     • REMOTE feed → GET {dataBaseUrl}/v1/feeds  (your NestJS API)
   Platform commerce lives behind a PlatformAdapter (ShopifyAdapter = AJAX API).

   Bootstrap (set these before/with the script; SDK auto-inits on load):
     window.SHOPPABLE_CONFIG = {
       tenant: "<api key or tenant id>",
       platform: "shopify",            // "shopify" | "mock"
       feed: "local",                  // "local" | "remote"
       dataBaseUrl: "https://api.you", // remote only
       showStoriesBubble: true
     };
     // LOCAL mode only — your reels:
     window.SHOPPABLE_FEED = [
       { src:"<hosted mp4/HLS>", poster:"", title:"Morning wriggles",
         products:["sunday-snuggle-sleepsuit"],          // Shopify product HANDLES
         placements:["carousel","stories","product"] }   // where it may appear
     ];
   ════════════════════════════════════════════════════════════════════════ */
(function (global) {
  "use strict";

  const shopRoot = () =>
    (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || "/";
  const currency = () =>
    (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || "INR";
  const money = (cents) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: currency(),
      maximumFractionDigits: 0 }).format((cents || 0) / 100);

  /* ── 1. PLATFORM ADAPTERS ───────────────────────────────────────────── */
  class PlatformAdapter {
    async getProduct(ref)             { throw new Error("not implemented"); }
    async addToCart(variantId, qty)   { throw new Error("not implemented"); }
    onCartChange(cb)                  { this._cb = cb; }
    _emit(n)                          { this._cb && this._cb(n); }
  }

  // Real Shopify storefront adapter — session-based AJAX API, no token.
  class ShopifyAdapter extends PlatformAdapter {
    async getProduct(handle) {
      const res = await fetch(`${shopRoot()}products/${handle}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`product ${handle} not found`);
      const p = await res.json();
      const variants = (p.variants || []).map((v) => ({
        id: v.id, title: v.title, available: v.available, priceFormatted: money(v.price),
      }));
      const firstAvail = variants.find((v) => v.available) || variants[0];
      // Shopify exposes `compare_at_price` per product/variant. When set and
      // higher than `price`, it's the MRP — we use it for the strikethrough
      // and "% off" pill that customers expect from WhatMore-style layouts.
      const compareCents = p.compare_at_price && p.compare_at_price > p.price ? p.compare_at_price : null;
      const percentOff = compareCents ? Math.round((1 - p.price / compareCents) * 100) : 0;
      return {
        id: handle,
        name: p.title,
        priceFormatted: money(p.price),
        compareFormatted: compareCents ? money(compareCents) : null,
        percentOff,
        image: (p.featured_image || (p.images && p.images[0]) || "").replace(/^http:/, "https:"),
        url: `${shopRoot()}products/${handle}`,
        variants,
        defaultVariant: firstAvail,
      };
    }
    async addToCart(variantId, qty = 1) {
      const add = await fetch(`${shopRoot()}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      });
      if (!add.ok) {
        const err = await add.json().catch(() => ({}));
        throw new Error(err.description || "Could not add to cart");
      }
      const cart = await (await fetch(`${shopRoot()}cart.js`)).json();
      this._emit(cart.item_count);
      try { document.dispatchEvent(new CustomEvent("cart:refresh")); } catch {}
      return { ok: true };
    }
  }

  // Demo adapter (platform:"mock") — in-memory cart, products come from the feed.
  class MockAdapter extends PlatformAdapter {
    constructor() { super(); this.n = 0; this.cache = {}; }
    register(p) { this.cache[p.id] = p; }
    async getProduct(id) { return this.cache[id]; }
    async addToCart() { await new Promise(r=>setTimeout(r,400)); this.n++; this._emit(this.n); return {ok:true}; }
  }

  /* ── 2. FEED CLIENTS ────────────────────────────────────────────────── */
  // LOCAL: filter window.SHOPPABLE_FEED by placement/context, no network.
  class LocalFeedClient {
    constructor(adapter) { this.adapter = adapter; }
    _all() { return (global.SHOPPABLE_FEED || []).map((v, i) => ({ id: v.id || `v${i}`, ...v })); }
    async getFeed({ placement, context }) {
      let vids = this._all().filter((v) => !v.placements || v.placements.includes(placement));
      if (placement === "product" && context) {
        vids = vids.filter((v) => (v.products || []).includes(context));
      }
      // demo adapter needs product records; build minimal ones from handles if mock
      if (this.adapter instanceof MockAdapter) {
        vids.forEach((v) => (v.products || []).forEach((h) =>
          this.adapter.register({ id: h, name: h, priceFormatted: "", image: "",
            variants: [{ id: h, title: "One size", available: true }], defaultVariant: { id: h, title: "One size" } })));
      }
      return { videos: vids };
    }
  }

  // REMOTE: GET {base}/v1/feeds?placement=&context=  with tenant api key header.
  class RemoteFeedClient {
    constructor({ base, tenant }) { this.base = base; this.tenant = tenant; }
    async getFeed({ placement, context }) {
      const u = new URL(`${this.base}/v1/feeds`);
      u.searchParams.set("placement", placement);
      if (context) u.searchParams.set("context", context);
      const res = await fetch(u, { headers: { "x-shoppable-key": this.tenant } });
      if (!res.ok) return { videos: [] };
      return res.json(); // { videos:[{id,src,poster,title,products:[handle]}] }
    }
  }

  /* ── 3. PLAYER (shared Reels-style lightbox) ────────────────────────── */
  const playerCSS = `
    :host{all:initial;display:block;width:100%;height:100%;font-family:var(--sv-font-body,system-ui)}
    .sv-root{position:relative;display:block;width:100%;height:100%}
    .scrim{position:relative;width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:rgba(28,20,16,.62);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);animation:f .25s ease;overflow:hidden}
    @keyframes f{from{opacity:0}} @keyframes up{from{transform:translateY(14px);opacity:0}}
    @keyframes svSlideInRight{from{transform:translateX(110%)}to{transform:translateX(0)}}
    @keyframes svSlideInLeft{from{transform:translateX(-110%)}to{transform:translateX(0)}}
    @keyframes svSlideOutLeft{from{transform:translateX(0)}to{transform:translateX(-110%)}}
    @keyframes svSlideOutRight{from{transform:translateX(0)}to{transform:translateX(110%)}}
    /* When a slide is in progress, multiple stages are siblings inside .scrim.
       We layer them absolutely so they animate over the same area. */
    .scrim:has(.slide-in-right,.slide-in-left) .stage{position:absolute;animation:none}
    .stage.slide-in-right{animation:svSlideInRight .55s cubic-bezier(.22,.61,.36,1) forwards}
    .stage.slide-in-left{animation:svSlideInLeft .55s cubic-bezier(.22,.61,.36,1) forwards}
    .stage.slide-out-left{animation:svSlideOutLeft .55s cubic-bezier(.22,.61,.36,1) forwards}
    .stage.slide-out-right{animation:svSlideOutRight .55s cubic-bezier(.22,.61,.36,1) forwards}
    .stage{position:relative;width:min(94vw,430px);height:min(86vh,764px);border-radius:26px;overflow:hidden;box-shadow:var(--sv-shadow,0 18px 50px -18px rgba(60,40,28,.45));background:#14100e;animation:up .3s cubic-bezier(.2,.8,.2,1)}
    .stage.full{width:100vw;height:100vh;height:100dvh;border-radius:0}
    video{width:100%;height:100%;object-fit:cover;display:block;background:#14100e}
    .veil{position:absolute;inset:0;pointer-events:none;background:linear-gradient(transparent 52%,rgba(0,0,0,.18) 70%,rgba(0,0,0,.72))}
    .top{position:absolute;top:0;left:0;right:0;padding:12px 14px 0;display:flex;gap:6px}
    .bar{flex:1;height:3px;border-radius:3px;background:rgba(255,255,255,.32);overflow:hidden}
    .bar>i{display:block;height:100%;width:0;background:#fff}.bar.done>i{width:100%}.bar.live>i{animation:grow linear forwards}
    @keyframes grow{to{width:100%}}
    .stage:not(.full) .top{display:none}
    .close,.mute{position:absolute;top:16px;z-index:3;width:36px;height:36px;border:none;border-radius:999px;background:rgba(20,16,14,.5);color:#fff;font-size:15px;cursor:pointer;display:grid;place-items:center;backdrop-filter:blur(4px)}
    .close{right:14px}.mute{right:58px}
    .nav{position:absolute;top:0;bottom:30%;width:33%;z-index:2;border:none;background:transparent;cursor:pointer}.nav.prev{left:0}.nav.next{right:0}
    .arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:4;width:40px;height:40px;border-radius:999px;border:none;background:rgba(255,255,255,.92);cursor:pointer;font-size:18px;box-shadow:0 6px 18px rgba(0,0,0,.3);display:grid;place-items:center}.arrow.l{left:-54px}.arrow.r{right:-54px}
    @media(max-width:560px){.arrow{display:none}}
    .shelf{position:absolute;left:0;right:0;bottom:12px;z-index:3}
    .cards{display:flex;gap:10px;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;padding:0 12px 2px;scrollbar-width:none}
    .cards::-webkit-scrollbar{display:none}
    .cards.single .card{flex-basis:calc(100% - 24px)}
    .card{flex:0 0 86%;scroll-snap-align:center;background:var(--sv-surface,#fff);border-radius:16px;padding:10px;display:flex;gap:10px;align-items:flex-start;box-shadow:0 12px 34px rgba(0,0,0,.34)}
    .card img{width:54px;height:66px;border-radius:10px;object-fit:cover;flex:none;background:#eee}
    .card .meta{flex:1;min-width:0;align-self:center}
    .card .name{font-family:var(--sv-font-display,serif);font-weight:600;font-size:.95rem;color:var(--sv-ink,#2a2320);line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .card .price-line{display:flex;align-items:baseline;gap:8px;margin-top:3px;flex-wrap:wrap}
    .card .price-line .price{font-weight:700;font-size:.9rem;color:var(--sv-ink,#2a2320)}
    .card .price-line .compare{font-size:.78rem;color:var(--sv-muted,#8a7d75);text-decoration:line-through}
    .card .price-line .pct{font-size:.68rem;font-weight:700;padding:2px 7px;border-radius:5px;background:#15803d;color:#fff;letter-spacing:.02em}
    .picker{position:relative;margin-top:7px;max-width:170px}
    .sel{appearance:none;-webkit-appearance:none;-moz-appearance:none;width:100%;font:inherit;font-size:.78rem;font-weight:600;color:var(--sv-ink,#2a2320);background:var(--sv-surface,#fff);border:1px solid var(--sv-line,#ddd);border-radius:8px;padding:6px 24px 6px 9px;cursor:pointer;line-height:1.2}
    .sel:focus{outline:2px solid var(--sv-accent,#b65a3c);outline-offset:1px}
    .chev{position:absolute;right:8px;top:50%;transform:translateY(-50%);font-size:.7rem;color:var(--sv-muted,#888);pointer-events:none}
    .cta{font:inherit;font-weight:700;font-size:.78rem;letter-spacing:.03em;text-transform:uppercase;border:none;cursor:pointer;white-space:nowrap;padding:11px 14px;border-radius:11px;background:var(--sv-accent,#b65a3c);color:var(--sv-accent-ink,#fff);align-self:center;flex:none}
    .cta:disabled{opacity:.55;cursor:default}
    .card.pdp{text-decoration:none;color:inherit}
    .card.pdp .cta{display:inline-flex;align-items:center;justify-content:center}
    .mosaic{width:54px;height:66px;border-radius:10px;overflow:hidden;flex:none;display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:1px;background:#eee}
    .mosaic img{width:100%;height:100%;object-fit:cover}
    .mosaic img:first-child:nth-last-child(3){grid-row:1/3}  /* 3-item mosaic: first spans both rows */
    /* Grid-only PDP card (multi-product reels): tidy thumb grid + CTA, no labels */
    .card.pdp-grid{align-items:center;padding:8px 8px 8px 8px}
    .mosaic-grid{width:80px;height:80px;border-radius:10px;overflow:hidden;flex:none;display:grid;gap:2px;background:#eee}
    .mosaic-grid .cell{overflow:hidden}
    .mosaic-grid .cell img{width:100%;height:100%;object-fit:cover;display:block}
    .mosaic-grid.g2{grid-template-columns:1fr 1fr;grid-template-rows:1fr}
    .mosaic-grid.g3{grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr}
    .mosaic-grid.g3 .cell:first-child{grid-row:1/3}
    .mosaic-grid.g4{grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr}
    .card.pdp-grid .cta{margin-left:auto}
    .toast{position:absolute;left:50%;bottom:50%;transform:translate(-50%,50%);z-index:6;background:rgba(20,16,14,.92);color:#fff;padding:12px 18px;border-radius:12px;font-weight:600;font-size:.9rem;opacity:0;transition:opacity .2s;text-align:center}
    .toast.show{opacity:1}
  `;

  // Inject a one-time scoped reset so our <dialog> fills the screen with no
  // default chrome. Namespaced to .sv-dialog so it can't affect the theme.
  /* (player host is now a plain div appended to <html> with !important inline
     styles + Shadow DOM — no theme CSS rule can override the overlay.) */


  class SvPlayer {
    constructor(ctx) { this.ctx = ctx; }
    open(list, opts = {}) {
      this.list = list;
      this.opts = Object.assign({ stories: false, purchase: "quickview", startIndex: 0 }, opts);
      this.i = this.opts.startIndex;
      // PLAIN DIV OVERLAY appended to <html>. Dawn (and many Shopify themes)
      // style <dialog> globally for their own cart drawer / quick-add modals,
      // which was making OUR <dialog> invisible. A custom div + Shadow DOM is
      // immune to that: shadow isolates internal styles, and !important inline
      // styles on the host pin it against any theme rule.
      const host = document.createElement("div");
      host.setAttribute("data-sv-player","");
      host.style.cssText =
        "all:initial!important;position:fixed!important;inset:0!important;"+
        "z-index:2147483647!important;display:block!important;width:100vw!important;"+
        "height:100vh!important;height:100dvh!important;pointer-events:auto!important;"+
        "opacity:1!important;visibility:visible!important;transform:none!important";
      host.attachShadow({ mode: "open" });
      const s = document.createElement("style"); s.textContent = playerCSS; host.shadowRoot.appendChild(s);
      this.root = document.createElement("div"); this.root.className = "sv-root"; host.shadowRoot.appendChild(this.root);
      this.host = host;
      // Append to <html> rather than <body> so theme wrappers / fixed headers /
      // sticky cart bars can't sit above us and we have one less ancestor that
      // could possibly carry a transform/filter that would break fixed positioning.
      document.documentElement.appendChild(host);
      document.documentElement.style.overflow = "hidden";
      this._render();
      this._onDocClick = (e)=>{
        // Click outside the stage closes. The click lands on host (light DOM)
        // when it's outside the shadow tree's stage area.
        const path = e.composedPath ? e.composedPath() : [];
        if(!path.includes(this.root.querySelector(".stage"))) this.close();
      };
      // Use a small delay before binding so the opening click doesn't immediately close it.
      setTimeout(()=>host.addEventListener("click", this._onDocClick), 0);
      this._key = (e)=>{
        if(e.key==="Escape") this.close();
        if(e.key==="ArrowRight") this.go(1);
        if(e.key==="ArrowLeft") this.go(-1);
      };
      window.addEventListener("keydown", this._key);
    }
    close(){
      window.removeEventListener("keydown", this._key);
      try{ this.host.remove(); }catch(e){}
      document.documentElement.style.overflow = "";
    }
    go(d){
      const n=this.i+d;
      if(n<0||n>=this.list.length){ if(this.opts.stories)this.close(); return; }
      this.i=n;
      if(this.opts.stories) this._render();
      else this._transition(d > 0 ? "next" : "prev");
    }
    // Carousel-mode advance: slide the new stage in from the side instead of
    // jump-cutting via _render. Gives the "filmstrip" feel users expect from
    // Reels/TikTok-style players. Stories mode keeps the instant render
    // because its progress bars own the pacing.
    _transition(dir){
      const scrim = this.root.querySelector(".scrim");
      const oldStage = this.root.querySelector(".stage");
      if(!scrim || !oldStage){ this._render(); return; }
      // Build a temp container with just the new stage HTML, then move it next
      // to the old stage and animate both.
      const tmp = document.createElement("div");
      // Render into a hidden root, swap pieces.
      const savedRoot = this.root;
      this.root = tmp;
      this._render();
      this.root = savedRoot;
      const newStage = tmp.querySelector(".stage");
      if(!newStage){ savedRoot.appendChild(tmp.firstChild); return; }
      // Insert new stage as a sibling, animate.
      newStage.classList.add(dir === "next" ? "slide-in-right" : "slide-in-left");
      oldStage.classList.add(dir === "next" ? "slide-out-left" : "slide-out-right");
      scrim.appendChild(newStage);
      // Move arrow buttons from temp into scrim (the temp render produced a
      // fresh pair; the existing pair on screen will be removed with old).
      const oldArrows = scrim.querySelectorAll(".arrow");
      oldArrows.forEach((a)=>a.remove());
      tmp.querySelectorAll(".arrow").forEach((a)=>scrim.appendChild(a));
      // Pause old video immediately so we don't fight for autoplay/audio.
      const oldVid = oldStage.querySelector("video"); if(oldVid){ try{oldVid.pause();}catch(_){} }
      // Clean up old stage after the slide finishes.
      const cleanup = ()=>{ try{ oldStage.remove(); }catch(_){} };
      newStage.addEventListener("animationend", cleanup, { once: true });
      // Safety net: in case animationend never fires.
      setTimeout(cleanup, 700);
    }
    _render() {
      const v=this.list[this.i], full=this.opts.stories;
      this.root.innerHTML = `
        <div class="scrim" data-scrim>
          <div class="stage ${full?"full":""}">
            ${full?`<div class="top">${this.list.map((_,k)=>`<div class="bar ${k<this.i?"done":k===this.i?"live":""}"><i></i></div>`).join("")}</div>`:""}
            <video src="${v.src}" ${v.poster?`poster="${v.poster}"`:""} playsinline autoplay muted></video>
            <div class="veil"></div>
            <button class="mute" title="Sound">🔇</button>
            <button class="close" title="Close">✕</button>
            <button class="nav prev"></button><button class="nav next"></button>
            <div class="shelf"></div><div class="toast"></div>
          </div>
          ${!full?`<button class="arrow l">‹</button><button class="arrow r">›</button>`:""}
        </div>`;
      const $=(s)=>this.root.querySelector(s);
      $(".close").onclick=()=>this.close();
      $(".scrim").addEventListener("click",(e)=>{ if(e.target.dataset.scrim!==undefined)this.close(); });
      $(".nav.prev").onclick=()=>this.go(-1); $(".nav.next").onclick=()=>this.go(1);
      const al=$(".arrow.l"),ar=$(".arrow.r"); if(al)al.onclick=()=>this.go(-1); if(ar)ar.onclick=()=>this.go(1);
      const video=$("video"), mute=$(".mute");
      mute.onclick=()=>{ video.muted=!video.muted; mute.textContent=video.muted?"🔇":"🔊"; };
      try { video.load(); } catch(e){}
      const tryPlay = () => video.play && video.play().catch(()=>{});
      tryPlay();
      video.addEventListener("loadedmetadata", tryPlay, { once: true });

      if(full){
        video.addEventListener("loadedmetadata",()=>{ const live=this.root.querySelector(".bar.live>i"); if(live)live.style.animationDuration=`${Math.min(video.duration||8,12)}s`; },{once:true});
      }

      // Auto-advance to the next reel — looped, WhatMore-style.
      // Two triggers (whichever fires first wins):
      //   1. `ended` — the canonical event
      //   2. `timeupdate` near the duration — covers MP4s that stall before
      //      reaching `ended`, which is common with some Shopify-CDN videos
      // We capture the index this reel was opened at so a late-firing event
      // from a previously-shown video can never advance to the wrong index.
      const myIndex = this.i;
      let advanced = false;
      const advance = (reason) => {
        if (advanced) return;
        if (this.i !== myIndex) return;   // a newer reel is already on screen
        advanced = true;
        console.log(`[shoppable] reel ${myIndex} done (${reason}) → next`);
        if (myIndex + 1 < this.list.length) this.go(1);
        else if (full) this.close();
        else { this.i = 0; this._transition("next"); }
      };
      video.addEventListener("ended", () => advance("ended"));
      video.addEventListener("timeupdate", () => {
        if (video.duration && video.duration - video.currentTime < 0.25) advance("near-end");
      });
      // Last-resort safety net: if metadata says the video is, say, 12s and
      // we're still on it 15s later, advance anyway. Catches networkState=3
      // (stalled) cases where neither `ended` nor `timeupdate` ever fire.
      const watchdog = setInterval(() => {
        if (advanced || this.i !== myIndex) { clearInterval(watchdog); return; }
        if (video.duration && video.currentTime >= video.duration + 1) {
          clearInterval(watchdog); advance("watchdog");
        }
      }, 1000);

      this._shelf(v,$(".shelf"));
    }
    async _shelf(video, shelf) {
      let products = [];
      try { products = await Promise.all((video.products||[]).map((h)=>this.ctx.adapter.getProduct(h))); }
      catch(e){ console.warn("[shoppable] product load failed", e); }
      products = products.filter(Boolean);
      if(!products.length) return;

      // Per-reel CTA mode (set in the feed). Defaults to "cart".
      //   cta:"cart" → one card per product with Add-to-Cart (swipe row if >1)
      //   cta:"pdp"  → goes to PDP instead of adding (good for twinning sets,
      //                bundles, or any "you need to pick multiple items" reel)
      const mode = (video.cta || "cart").toLowerCase();
      const row = document.createElement("div");

      if (mode === "pdp") {
        if (products.length === 1) {
          // Single product → "View product" → that product's PDP
          const p = products[0];
          row.className = "cards single";
          row.appendChild(this._pdpCard({
            image: p.image, name: p.name, priceFormatted: p.priceFormatted,
            url: video.ctaUrl || p.url, label: video.ctaLabel || "View product",
            reel: video,
          }));
        } else {
          // Multiple products → one "Shop this look" card. We show a tidy
          // mosaic of product thumbs (no item count, no product name) — the
          // grid speaks for itself; clicking takes them to ctaUrl (collection).
          row.className = "cards single";
          const thumbs = products.slice(0,4).map((p)=>p.image).filter(Boolean);
          row.appendChild(this._pdpCard({
            mosaicOnly: true,
            mosaic: thumbs,
            url: video.ctaUrl || products[0].url,
            label: video.ctaLabel || "Shop this look",
            reel: video,
          }));
        }
      } else {
        row.className = "cards" + (products.length===1 ? " single" : "");
        products.forEach((p)=>row.appendChild(this._card(p)));
      }
      shelf.appendChild(row);
    }
    // "View product" / "Shop this look" card — no variant selection, just routes
    // to a PDP (or collection URL). Used when a reel sets cta:"pdp".
    // When clicked, we stash the reel + destination in sessionStorage so the
    // destination page can pick it up and float a mini-reel player.
    _pdpCard({ image, name, priceFormatted, url, label, mosaic, mosaicOnly, reel }) {
      const card = document.createElement("a");
      card.className = "card pdp" + (mosaicOnly ? " pdp-grid" : "");
      card.href = url || "#";

      if (mosaicOnly && mosaic && mosaic.length) {
        // Tidy grid of up to 4 product thumbs — no name/price/count clutter.
        const cells = mosaic.slice(0,4).map((src)=>`<div class="cell"><img src="${src}" alt=""></div>`).join("");
        card.innerHTML = `<div class="mosaic-grid g${Math.min(mosaic.length,4)}">${cells}</div>`+
          `<span class="cta">${label} →</span>`;
      } else {
        const thumb = (mosaic && mosaic.length > 1)
          ? `<div class="mosaic">${mosaic.map((src)=>`<img src="${src}" alt="">`).join("")}</div>`
          : `<img src="${image||""}" alt="">`;
        card.innerHTML = `
          ${thumb}
          <div class="meta">
            <div class="name">${name||""}</div>
            <div class="price">${priceFormatted||""}</div>
          </div>
          <span class="cta">${label} →</span>`;
      }

      // On click: remember which reel was used to land on the destination URL.
      // The destination page reads this on load and shows a mini-reel player.
      card.addEventListener("click", () => {
        if (!reel || !url) return;
        try {
          sessionStorage.setItem("sv-mini-reel", JSON.stringify({
            forUrl: url,
            video: { id: reel.id, src: reel.src, poster: reel.poster, title: reel.title, products: reel.products },
            at: Date.now(),
          }));
        } catch (_) {}
      });
      return card;
    }
    // WhatMore-style: the product card with Add-to-cart sits right on the video.
    // Multi-variant products show a size picker; the button enables once a
    // size is picked. Single-variant products add in one tap.
    _card(p){
      const card=document.createElement("div"); card.className="card";
      const variants = p.variants || [];
      const multi = variants.length > 1;
      let chosen = multi ? null : (p.defaultVariant || variants[0] || { id: p.id, title: "" });
      // Native <select> for variants: compact, scales to any number of sizes,
      // and triggers the OS picker on mobile (much better than tall chip grids).
      const opts = variants.map((v,k)=>`<option value="${k}" ${v.available?"":"disabled"}>${v.title}${v.available?"":" — sold out"}</option>`).join("");
      card.innerHTML=`
        <img src="${p.image}" alt="">
        <div class="meta">
          <div class="name">${p.name}</div>
          <div class="price-line">
            <span class="price">${p.priceFormatted||""}</span>
            ${p.compareFormatted?`<span class="compare">${p.compareFormatted}</span>`:""}
            ${p.percentOff?`<span class="pct">${p.percentOff}% OFF</span>`:""}
          </div>
          ${multi?`<div class="picker"><select class="sel" aria-label="Choose a size"><option value="" selected disabled>Select size</option>${opts}</select><span class="chev">▾</span></div>`:""}
        </div>
        <button class="cta">Add to cart</button>`;
      const cta=card.querySelector(".cta");
      if(multi){
        cta.disabled=true;
        const sel=card.querySelector(".sel");
        sel.addEventListener("change",()=>{
          const k=sel.value;
          if(k===""){ chosen=null; cta.disabled=true; return; }
          chosen=variants[+k]; cta.disabled=false;
        });
      }
      cta.onclick=async()=>{
        if(!chosen)return;
        cta.disabled=true; cta.textContent="Adding…";
        try{ await this.ctx.adapter.addToCart(chosen.id,1); this._toast(`Added — ${p.name}${chosen.title?` (${chosen.title})`:""}`); cta.textContent="✓ Added"; }
        catch(err){ this._toast(err.message||"Try again"); cta.textContent="Add to cart"; cta.disabled=false; }
      };
      return card;
    }
    _toast(m){ const t=this.root.querySelector(".toast"); t.textContent=m; t.classList.add("show"); setTimeout(()=>t.classList.remove("show"),2000); }
  }

  /* ── 4. PLACEMENT ELEMENTS ──────────────────────────────────────────── */
  const elCSS=`
    :host{all:initial;display:block;font-family:var(--sv-font-body,system-ui)}
    .rail{display:flex;gap:16px;overflow-x:auto;padding:8px 0 18px;scroll-snap-type:x proximity;scrollbar-width:thin;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}
    .rail::-webkit-scrollbar{height:6px}.rail::-webkit-scrollbar-thumb{background:var(--sv-line,#ddd);border-radius:6px}
    .tile-wrap{flex:none;width:min(288px,78vw);scroll-snap-align:start;display:flex;flex-direction:column}
    .tile{position:relative;width:100%;aspect-ratio:9/16;border-radius:18px;overflow:hidden;cursor:pointer;background:#14100e;box-shadow:var(--sv-shadow,0 12px 30px rgba(0,0,0,.2))}
    .tile video{width:100%;height:100%;object-fit:cover;pointer-events:none}
    /* Below-tile info row (WhatMore-style): thumb + name/price/discount */
    .info{display:flex;align-items:flex-start;gap:10px;padding:10px 4px 0;min-height:62px}
    .info:empty{display:none}
    .info img{width:46px;height:54px;border-radius:8px;object-fit:cover;flex:none;background:#eee}
    .info-text{flex:1;min-width:0;line-height:1.25}
    .info-name{font-weight:600;font-size:.84rem;color:var(--sv-ink,#2a2320);display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis}
    .info-prices{display:flex;align-items:baseline;gap:8px;margin-top:3px}
    .info-prices .price{font-weight:700;font-size:.9rem;color:var(--sv-ink,#2a2320)}
    .info-prices .compare{font-size:.78rem;color:var(--sv-muted,#8a7d75);text-decoration:line-through}
    .info .pct{display:inline-block;margin-top:5px;font-size:.7rem;font-weight:700;padding:3px 8px;border-radius:6px;background:#15803d;color:#fff;letter-spacing:.01em}
    /* Multi-product info: horizontal swipe of compact product chips */
    .info-multi{padding:10px 0 0;display:block;min-height:auto}
    .info-multi .mini-row{display:flex;gap:8px;overflow-x:auto;padding:0 4px 4px;scrollbar-width:thin;-webkit-overflow-scrolling:touch;scroll-snap-type:x proximity}
    .info-multi .mini-row::-webkit-scrollbar{height:4px}
    .info-multi .mini-row::-webkit-scrollbar-thumb{background:var(--sv-line,#ddd);border-radius:4px}
    .info-multi .mini{flex:0 0 auto;display:flex;align-items:center;gap:8px;padding:6px;border:1px solid var(--sv-line,#e7ddd0);border-radius:10px;background:var(--sv-surface,#fff);max-width:200px;scroll-snap-align:start}
    .info-multi .mini img{width:36px;height:44px;border-radius:6px;object-fit:cover;flex:none;background:#eee}
    .info-multi .mini-meta{min-width:0;line-height:1.2}
    .info-multi .mini-name{font-weight:600;font-size:.74rem;color:var(--sv-ink,#2a2320);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:130px}
    .info-multi .mini-price{display:flex;align-items:baseline;gap:6px;margin-top:2px}
    .info-multi .mini-price .price{font-weight:700;font-size:.76rem;color:var(--sv-ink,#2a2320)}
    .info-multi .mini-pct{font-size:.62rem;font-weight:700;padding:1px 5px;border-radius:4px;background:#15803d;color:#fff;letter-spacing:.02em}
    .tile:hover{transform:translateY(-3px);transition:transform .2s}
    .inline{display:flex;gap:14px;align-items:center;padding:12px;border:1px solid var(--sv-line,#e7ddd0);border-radius:16px;background:var(--sv-surface,#fff);cursor:pointer;max-width:380px}
    .inline video{width:64px;height:84px;border-radius:11px;object-fit:cover;flex:none;pointer-events:none}
    .inline .t{font-weight:700;font-size:.92rem}.inline .s{color:var(--sv-muted,#888);font-size:.82rem;margin-top:2px}
    .inline .play{margin-left:auto;width:36px;height:36px;border-radius:999px;background:var(--sv-accent,#b65a3c);color:#fff;display:grid;place-items:center;flex:none}
    .bubble{position:fixed;right:18px;top:58%;transform:translateY(-50%);z-index:9998;width:70px;height:70px;border-radius:999px;cursor:pointer;padding:3px;background:conic-gradient(from 210deg,var(--sv-accent,#b65a3c),#e7b78f,var(--sv-accent,#b65a3c));box-shadow:var(--sv-shadow,0 12px 30px rgba(0,0,0,.3));animation:pulse 2.6s ease-in-out infinite}
    @keyframes pulse{0%,100%{box-shadow:0 12px 30px rgba(182,90,60,.35)}50%{box-shadow:0 12px 40px rgba(182,90,60,.6)}}
    .bubble video{width:100%;height:100%;border-radius:999px;object-fit:cover;border:2px solid var(--sv-surface,#fff)}
    .bubble .lab{position:absolute;bottom:-7px;left:50%;transform:translateX(-50%);background:var(--sv-ink,#2a2320);color:#fff;font-size:.62rem;font-weight:700;padding:2px 8px;border-radius:999px;white-space:nowrap}
    .bubble .x{position:absolute;top:-6px;right:-6px;width:22px;height:22px;border-radius:999px;border:none;background:var(--sv-ink,#2a2320);color:#fff;font-size:12px;line-height:1;cursor:pointer;display:grid;place-items:center;box-shadow:0 2px 8px rgba(0,0,0,.35);padding:0;animation:none;font-family:inherit}
    .bubble .x:hover{background:#000}
    @media(max-width:560px){.bubble{width:60px;height:60px;right:14px;top:60%}}
    /* Floating MINI-REEL on destination pages — small portrait video card. */
    .mini-reel{position:fixed;right:18px;bottom:18px;z-index:9998;width:140px;aspect-ratio:9/16;border-radius:16px;overflow:hidden;cursor:pointer;background:#14100e;box-shadow:0 12px 30px rgba(0,0,0,.35);border:2px solid var(--sv-surface,#fff);transition:transform .2s}
    .mini-reel:hover{transform:translateY(-3px)}
    .mini-reel video{width:100%;height:100%;object-fit:cover;pointer-events:none;display:block}
    .mini-reel .x{position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:999px;border:none;background:rgba(20,16,14,.7);color:#fff;font-size:12px;line-height:1;cursor:pointer;display:grid;place-items:center;padding:0;font-family:inherit;backdrop-filter:blur(4px)}
    .mini-reel .x:hover{background:#000}
    .mini-reel .play-ind{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:36px;height:36px;border-radius:999px;background:rgba(255,255,255,.92);color:#2a2320;font-size:14px;display:grid;place-items:center;pointer-events:none;opacity:0;transition:opacity .2s}
    .mini-reel:hover .play-ind{opacity:1}
    @media(max-width:560px){.mini-reel{width:110px;right:12px;bottom:12px}}
  `;
  function baseEl(){
    return class extends HTMLElement{
      connectedCallback(){
        if(this._m)return; this._m=true;
        this.attachShadow({mode:"open"});
        const s=document.createElement("style"); s.textContent=elCSS; this.shadowRoot.appendChild(s);
        this.wrap=document.createElement("div"); this.shadowRoot.appendChild(this.wrap);
        Shoppable._ready.then(()=>this.mount().catch((e)=>console.warn("[shoppable]",e)));
      }
      // WhatMore-style layout: video tile on top, product info row BELOW (not
      // overlaid on the reel). This keeps the visual clean and shows price /
      // discount info that customers expect on a homepage carousel.
      tile(v){
        const wrap=document.createElement("div"); wrap.className="tile-wrap";
        const t=document.createElement("div"); t.className="tile";
        t.innerHTML=`<video data-src="${v.src}" ${v.poster?`poster="${v.poster}"`:""} muted loop playsinline preload="none"></video>`;
        const info=document.createElement("div"); info.className="info"; info.setAttribute("aria-hidden","true");
        wrap.appendChild(t); wrap.appendChild(info);
        return wrap;
      }
      // Populates the below-tile info area for a reel:
      //   • 1 product   → full row (thumb + name + price + strikethrough + % off)
      //   • 2+ products → horizontal swipe of compact product chips, mirroring
      //                   the multi-product shelf inside the player
      // Fetched in parallel; failures hide that product silently. If all fail
      // the whole info area is removed and the tile still works on click.
      async hydrateChip(tileWrap, v){
        const info = tileWrap.querySelector(".info");
        if(!info || !v.products || !v.products.length){ info && info.remove(); return; }
        let products;
        try { products = await Promise.all(v.products.map((h)=>Shoppable.adapter.getProduct(h).catch(()=>null))); }
        catch(e) { products = []; }
        products = (products||[]).filter(Boolean);
        if(!products.length){ info.remove(); return; }

        if(products.length === 1){
          const p = products[0];
          info.innerHTML =
            `<img src="${p.image||""}" alt="" onerror="this.style.visibility='hidden'">`+
            `<div class="info-text">`+
              `<div class="info-name">${p.name}</div>`+
              `<div class="info-prices">`+
                `<span class="price">${p.priceFormatted}</span>`+
                (p.compareFormatted?`<span class="compare">${p.compareFormatted}</span>`:"")+
              `</div>`+
              (p.percentOff?`<div class="pct">${p.percentOff}% off</div>`:"")+
            `</div>`;
        } else {
          info.classList.add("info-multi");
          info.innerHTML =
            `<div class="mini-row">`+
              products.map((p)=>(
                `<div class="mini" title="${p.name.replace(/"/g,"&quot;")}">`+
                  `<img src="${p.image||""}" alt="" onerror="this.style.visibility='hidden'">`+
                  `<div class="mini-meta">`+
                    `<div class="mini-name">${p.name}</div>`+
                    `<div class="mini-price">`+
                      `<span class="price">${p.priceFormatted}</span>`+
                      (p.percentOff?`<span class="mini-pct">${p.percentOff}% off</span>`:"")+
                    `</div>`+
                  `</div>`+
                `</div>`
              )).join("")+
            `</div>`;
        }
      }
    };
  }
  class SvCarousel extends baseEl(){
    async mount(){
      const feed=await Shoppable.data.getFeed({placement:"carousel",context:this.getAttribute("collection")});
      if(!feed.videos.length)return;
      const rail=document.createElement("div"); rail.className="rail";
      // PERF: build tiles WITHOUT triggering work (no video src yet, no product
      // fetch). The IntersectionObserver below promotes a tile to "active":
      //   • assigns <video> src so the browser starts downloading
      //   • fires hydrateChip() which calls /products/{handle}.js
      // Tiles that never enter the viewport (off the right edge of the rail,
      // or below the fold) cost essentially zero network on page load.
      feed.videos.forEach((v,i)=>{
        const t=this.tile(v);
        t._reel = v;  // stash for the observer
        t.onclick=()=>{ try{ Shoppable.player().open(feed.videos,{startIndex:i,purchase:"quickview"}); }catch(e){ console.error("[shoppable] could not open player",e); } };
        rail.appendChild(t);
      });
      this.wrap.appendChild(rail);
      this._observe(rail);
    }
    // Single observer: lazy-loads video src + product info on first reveal,
    // then autoplays/pauses based on viewport thereafter. rootMargin pre-warms
    // tiles ~200px before they enter view so users don't see a blank flash.
    _observe(rail){
      const tiles=[...rail.querySelectorAll(".tile-wrap")];
      if(!("IntersectionObserver" in window)){
        // Fallback: hydrate everything synchronously (old behavior).
        tiles.forEach((t)=>this._activate(t,true));
        return;
      }
      // Tight rootMargin: only activate tiles that are ACTUALLY visible. With
      // large source videos (uncompressed reels can be 20+ MB each), pre-warming
      // off-screen tiles costs real bandwidth on every page load.
      const io=new IntersectionObserver((entries)=>{
        entries.forEach((e)=>{
          const t=e.target, vid=t.querySelector("video");
          if(e.isIntersecting){
            this._activate(t,false);
            vid && vid.play().catch(()=>{});
          } else {
            vid && vid.pause();
          }
        });
      },{root:null,rootMargin:"0px",threshold:0.5});
      tiles.forEach((t)=>io.observe(t));
    }
    _activate(tileWrap, eager){
      if(tileWrap._activated) return;
      tileWrap._activated = true;
      const vid = tileWrap.querySelector("video");
      const src = vid && vid.getAttribute("data-src");
      if(vid && src){
        vid.setAttribute("src", src);
        vid.removeAttribute("data-src");
        // Now that the user is about to see it, allow metadata to load.
        vid.preload = "metadata";
      }
      // Kick off product hydration only when the tile becomes visible.
      this.hydrateChip(tileWrap, tileWrap._reel);
    }
  }
  class SvProductWidget extends baseEl(){
    async mount(){
      const pid=this.getAttribute("product-id");
      const feed=await Shoppable.data.getFeed({placement:"product",context:pid});
      if(!feed.videos.length)return;
      const v=feed.videos[0];
      const box=document.createElement("div"); box.className="inline";
      box.innerHTML=`<video src="${v.src}" ${v.poster?`poster="${v.poster}"`:""} muted loop playsinline autoplay preload="metadata"></video>
        <div><div class="t">Watch &amp; shop this style</div><div class="s">${v.title||""}</div></div><div class="play">▸</div>`;
      box.querySelector("video").play().catch(()=>{});
      box.onclick=()=>Shoppable.player().open(feed.videos,{startIndex:0,purchase:"inline"});
      this.wrap.appendChild(box);
    }
  }
  class SvStoriesBubble extends baseEl(){
    async mount(){
      // Respect previous "X" dismissal during this browsing session.
      try { if(sessionStorage.getItem("sv-bubble-closed")==="1") return; } catch(e){}
      const feed=await Shoppable.data.getFeed({placement:"stories",context:null});
      if(!feed.videos.length)return;
      const b=document.createElement("div"); b.className="bubble";
      b.innerHTML=`
        <video src="${feed.videos[0].src}" ${feed.videos[0].poster?`poster="${feed.videos[0].poster}"`:""} muted loop playsinline preload="metadata" autoplay></video>
        <span class="lab">Stories</span>
        <button class="x" aria-label="Hide stories">✕</button>`;
      // Open the player when the bubble (but not the ✕) is clicked.
      b.addEventListener("click",(e)=>{
        if(e.target.closest(".x")) return;
        Shoppable.player().open(feed.videos,{stories:true,purchase:"quickview"});
      });
      // Dismiss for this session only — comes back on next visit / new tab.
      b.querySelector(".x").addEventListener("click",(e)=>{
        e.stopPropagation();
        try { sessionStorage.setItem("sv-bubble-closed","1"); } catch(_){}
        this.remove();
      });
      this.wrap.appendChild(b);
    }
  }

  /**
   * Floating MINI-REEL — appears on a destination page (e.g. the collection
   * page a "Shop this look" reel pointed to) and plays the originating reel
   * in a small portrait card pinned bottom-right. Reads the handoff written
   * by _pdpCard into sessionStorage. Tapping it opens the full player; ✕
   * hides it for the current page-view only — reload/navigate brings it back
   * as long as the handoff is still in sessionStorage (i.e. same tab session).
   *
   * Auto-shows on any page when a valid handoff for the current URL exists,
   * so the merchant doesn't need to place a custom element manually.
   */
  class SvMiniReel extends baseEl(){
    async mount(){
      // Two ways this element resolves what reel to show:
      //   1. Handoff — sessionStorage entry written by _pdpCard when a
      //      customer clicked "Shop this look". Highest priority: this is the
      //      exact reel that brought them here.
      //   2. PDP fallback — no handoff, but the current page is a product page,
      //      and there's a reel tagged with this product's handle in the feed.
      //      Auto-select the first matching reel so every PDP shows a mini-reel
      //      even for customers who arrived via search / direct link / another
      //      collection.
      let v = null;

      // Path 1: handoff
      try {
        const handoff = JSON.parse(sessionStorage.getItem("sv-mini-reel") || "null");
        if (handoff && handoff.video && handoff.video.src) {
          const target = new URL(handoff.forUrl, location.origin);
          if (target.pathname === location.pathname) v = handoff.video;
        }
      } catch(_) {}

      // Path 2: PDP fallback — only if handoff didn't match
      if (!v) {
        const productHandle = this._detectProductHandle();
        if (!productHandle) return;
        try {
          const feed = await Shoppable.data.getFeed({ placement: "product", context: productHandle });
          if (feed && feed.videos && feed.videos.length) v = feed.videos[0];
        } catch(_) {}
      }

      if (!v || !v.src) return;
      const card = document.createElement("div"); card.className = "mini-reel";
      card.innerHTML = `
        <video src="${v.src}" ${v.poster?`poster="${v.poster}"`:""} muted loop playsinline autoplay preload="metadata"></video>
        <button class="x" aria-label="Hide reel">✕</button>
        <span class="play-ind" aria-hidden="true">▸</span>`;
      card.querySelector("video").play().catch(()=>{});
      card.addEventListener("click",(e)=>{
        if(e.target.closest(".x")) return;
        // Open the full player at this reel. We synthesize a single-item list
        // so the player has something to render even if a full feed isn't on
        // this page (the collection page won't have the carousel mounted).
        Shoppable.player().open([v], { startIndex: 0, purchase: "quickview" });
      });
      card.querySelector(".x").addEventListener("click",(e)=>{
        e.stopPropagation();
        // Hide for this page-view only. Reload / navigation will re-render
        // the mini-reel from the same sessionStorage handoff (or from the
        // PDP fallback if we're on a product page).
        this.remove();
      });
      this.wrap.appendChild(card);
    }
    // Detect the current product handle from the URL, e.g. /products/foo-bar
    // → "foo-bar". Returns null if we're not on a product page. Compatible
    // with Shopify's URL patterns including localized paths (/en/products/…).
    _detectProductHandle() {
      const m = location.pathname.match(/\/products\/([^\/\?#]+)/);
      return m ? m[1] : null;
    }
  }

  /* ── 5. BOOTSTRAP + AUTO-INIT ───────────────────────────────────────── */
  const Shoppable={
    init(cfg){
      cfg=cfg||{};
      this.adapter = cfg.platform==="shopify" ? new ShopifyAdapter() : new MockAdapter();
      this.data = cfg.feed==="remote"
        ? new RemoteFeedClient({ base:(cfg.dataBaseUrl||"").replace(/\/$/,""), tenant:cfg.tenant })
        : new LocalFeedClient(this.adapter);
      if(typeof cfg.onCartChange==="function") this.adapter.onCartChange(cfg.onCartChange);
      this._playerFactory=()=>new SvPlayer({adapter:this.adapter});
      if(!customElements.get("sv-carousel"))       customElements.define("sv-carousel",SvCarousel);
      if(!customElements.get("sv-product-widget")) customElements.define("sv-product-widget",SvProductWidget);
      if(!customElements.get("sv-stories-bubble"))  customElements.define("sv-stories-bubble",SvStoriesBubble);
      if(!customElements.get("sv-mini-reel"))       customElements.define("sv-mini-reel",SvMiniReel);
      if(cfg.showStoriesBubble && !document.querySelector("sv-stories-bubble")){
        document.body.appendChild(document.createElement("sv-stories-bubble"));
      }
      // Mini-reel auto-mounts site-wide; it renders nothing unless there's a
      // handoff in sessionStorage matching the current page's pathname.
      if(!document.querySelector("sv-mini-reel")){
        document.body.appendChild(document.createElement("sv-mini-reel"));
      }
      this._resolve(); this.config=cfg; return this;
    },
    player(){ return this._playerFactory(); },
  };
  Shoppable._ready=new Promise((r)=>(Shoppable._resolve=r));
  global.Shoppable=Shoppable;

  // auto-init when a config global is present (used by both Dawn snippet & app embed)
  const boot=()=>{ if(global.SHOPPABLE_CONFIG && !Shoppable.config) Shoppable.init(global.SHOPPABLE_CONFIG); };
  if(document.readyState==="loading") document.addEventListener("DOMContentLoaded",boot);
  else boot();

})(window);